import React, { useState, useEffect } from "react";
import QRCode from "react-qr-code";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/hooks/useAppStore";
import { generateShareableUrl, copyToClipboard } from "@/lib/utils";
import { Copy, Check } from "lucide-react";
import { Input } from "../ui/input";
import { useTranslation } from "@/lib/translate";

interface ShareRoomDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ShareRoomDialog: React.FC<ShareRoomDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const roomId = useAppStore((state) => state.roomId);
  const preferredLanguage = useAppStore((state) => state.preferredLanguage);
  const showQosMessage = useAppStore((state) => state.showQosMessage);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const t = useTranslation(preferredLanguage);

  useEffect(() => {
    if (open && roomId) {
      const url = generateShareableUrl(
        roomId,
        window.location.origin,
        { presentation: "false" }
      );
      setShareUrl(url);
      setCopied(false); // Reset copied state when opening
    } else {
      setShareUrl(""); // Clear URL if dialog closed or no room ID
    }
  }, [open, roomId]);

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await copyToClipboard(shareUrl);
      setCopied(true);
      showQosMessage(t("Link copied to clipboard!"), 2000);
      setTimeout(() => setCopied(false), 2000); // Reset icon after 2s
    } catch (error) {
      showQosMessage(t("Failed to copy link."), 3000);
      console.error(t("Copy failed:"), error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-neutral-800 border-neutral-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-center text-lg">
            多言語同時翻訳サービス
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            {t("Join Chotto Chat")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center justify-center gap-4 py-4">
          {shareUrl ? (
            <div className="bg-white p-2 rounded-lg max-w-[200px] max-h-[200px]">
              {" "}
              {/* Adjust size as needed */}
              <QRCode
                value={shareUrl}
                size={192} // Ensure this matches container size roughly
                level="M" // Error correction level
                bgColor="#FFFFFF"
                fgColor="#000000"
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              />
            </div>
          ) : (
            <p className="text-neutral-400">Generating share link...</p>
          )}

          {shareUrl && (
            <div className="w-full px-4">
              <p className="text-xs text-center mb-4 text-neutral-300">
                {t("A website will open")}<br /> {t("You can also join the chat using the following URL")}
              </p>
              <div className="flex items-center gap-2 rounded-md bg-neutral-700 p-2 text-sm overflow-hidden">
                <Input value={shareUrl} className="flex-1 text-neutral-100 border-none bg-transparent" tabIndex={-1} />
                <Button
                  variant="ghost"
                  size="sm"
                  className="px-2 text-white hover:bg-neutral-600"
                  onClick={handleCopy}
                  aria-label="Copy link"
                >
                  {copied ? (
                    <Check size={16} className="text-green-400" />
                  ) : (
                    <Copy size={16} />
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="sm:justify-center">
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            {t("Close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
